import { useState, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext.jsx';
import { fonts as fontsApi } from '../api/client.js';

const SIZES = [16, 24, 36, 48];

export default function ComparisonPanel({ fontIds = [], onClose }) {
  const { theme } = useTheme();
  const [fonts, setFonts] = useState([]);
  const [sampleText, setSampleText] = useState('The quick brown fox jumps over the lazy dog');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (fontIds.length === 0) return;
    setLoading(true);
    Promise.all(fontIds.slice(0, 3).map(id => fontsApi.get(id)))
      .then(setFonts)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [fontIds]);

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 200,
      background: theme.overlay, display: 'flex',
      alignItems: 'center', justifyContent: 'center',
    }} onClick={onClose}>
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: '90%', maxWidth: 900, maxHeight: '85vh',
          background: theme.bgElevated, borderRadius: 16,
          border: `1px solid ${theme.borderMedium}`,
          boxShadow: theme.shadow, overflow: 'hidden',
          display: 'flex', flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '16px 20px', borderBottom: `1px solid ${theme.border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: theme.text }}>Font Comparison</h3>
          <button
            onClick={onClose}
            style={{
              background: theme.bgInput, border: 'none', borderRadius: 6,
              color: theme.textMuted, width: 28, height: 28, cursor: 'pointer', fontSize: 13,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >✕</button>
        </div>

        {/* Sample text input */}
        <div style={{ padding: '12px 20px', borderBottom: `1px solid ${theme.borderLight}` }}>
          <input
            type="text"
            value={sampleText}
            onChange={e => setSampleText(e.target.value)}
            placeholder="Type sample text..."
            style={{
              width: '100%', padding: '8px 12px',
              background: theme.bgInput, border: `1px solid ${theme.borderMedium}`,
              borderRadius: 8, color: theme.text, fontSize: 13, outline: 'none',
              fontFamily: 'inherit',
            }}
          />
        </div>

        {/* Comparison body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
          {loading ? (
            <div style={{ color: theme.textMuted, fontSize: 12, textAlign: 'center', padding: 40 }}>Loading fonts...</div>
          ) : (
            <>
              {/* Side-by-side at multiple sizes */}
              {SIZES.map(size => (
                <div key={size} style={{ marginBottom: 20 }}>
                  <div style={{
                    fontSize: 10, fontWeight: 600, color: theme.textGhost, marginBottom: 8,
                    textTransform: 'uppercase', letterSpacing: '0.06em',
                  }}>{size}px</div>
                  <div style={{ display: 'grid', gridTemplateColumns: `repeat(${fonts.length}, 1fr)`, gap: 12 }}>
                    {fonts.map(f => (
                      <div key={f.id} style={{
                        padding: 12, background: theme.bgTertiary,
                        borderRadius: 8, border: `1px solid ${theme.borderLight}`,
                      }}>
                        <div style={{
                          fontSize: 10, fontWeight: 600, color: theme.textMuted, marginBottom: 6,
                        }}>{f.name}</div>
                        <div style={{
                          fontFamily: `'${f.family}', serif`, fontSize: size,
                          color: theme.text, lineHeight: 1.3,
                          overflow: 'hidden', textOverflow: 'ellipsis',
                        }}>{sampleText}</div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              {/* Metadata comparison table */}
              <div style={{ marginTop: 24 }}>
                <div style={{
                  fontSize: 10, fontWeight: 600, color: theme.textGhost, marginBottom: 8,
                  textTransform: 'uppercase', letterSpacing: '0.06em',
                }}>Metadata</div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', padding: '6px 8px', color: theme.textMuted, fontWeight: 600, borderBottom: `1px solid ${theme.border}` }}>Property</th>
                      {fonts.map(f => (
                        <th key={f.id} style={{ textAlign: 'left', padding: '6px 8px', color: theme.textSecondary, fontWeight: 600, borderBottom: `1px solid ${theme.border}` }}>{f.name}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {['category', 'designer', 'style_count', 'size_display', 'source', 'license_type'].map(prop => (
                      <tr key={prop}>
                        <td style={{ padding: '5px 8px', color: theme.textMuted, borderBottom: `1px solid ${theme.borderLight}`, textTransform: 'capitalize' }}>
                          {prop.replace(/_/g, ' ')}
                        </td>
                        {fonts.map(f => (
                          <td key={f.id} style={{ padding: '5px 8px', color: theme.textSecondary, borderBottom: `1px solid ${theme.borderLight}` }}>
                            {f[prop] || '—'}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
